import { useState, useEffect, useRef, memo, useCallback } from 'react';
import { cn, BERASTAYS_IMAGE_PLACEHOLDER } from '@/lib/utils';
import { Capacitor } from '@capacitor/core';
import { Home } from 'lucide-react';
import { isImageLoaded, markImageLoaded } from '@/lib/imageCache';

// Placeholder oficial Berastays (estável, branded, sem aleatoriedade)
const BRAND_PLACEHOLDER = BERASTAYS_IMAGE_PLACEHOLDER;


interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  priority?: boolean;
  aspectRatio?: '4/3' | '16/9' | '1/1' | '3/2' | 'none';
  objectFit?: 'cover' | 'contain' | 'fill';
  onLoad?: () => void;
  onError?: () => void;
  placeholderColor?: string;
  sizes?: string;
}

/**
 * Componente de Imagem Otimizado - Versão Melhorada
 * 
 * Correções:
 * - Timeout removido: não força placeholder por tempo
 * - Apenas mostra placeholder em erro REAL de carregamento
 * - Retry automático com delay exponencial (1s, 2s, 4s)
 * - Lazy loading otimizado com Intersection Observer
 * - Fallback visual com botão de recarregar
 */
const OptimizedImage = memo(({
  src,
  alt,
  className,
  containerClassName,
  priority = false,
  aspectRatio = '4/3',
  objectFit = 'cover',
  onLoad,
  onError,
  placeholderColor = 'transparent',
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
}: OptimizedImageProps) => {
  const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  // Inicializa já carregado se a URL estiver no cache (evita flash)
  const [isLoaded, setIsLoaded] = useState(() => isImageLoaded((src || '').trim()));
  const [isInView, setIsInView] = useState(priority || isAndroidNative);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef<HTMLDivElement>(null);
  const imgElRef = useRef<HTMLImageElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset state quando src muda — mantém loaded se já estiver em cache
  useEffect(() => {
    const cached = isImageLoaded((src || '').trim());
    setIsLoaded(cached);
    setHasError(false);
    setRetryCount(0);
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
  }, [src]);

  // Intersection Observer para lazy loading
  useEffect(() => {
    if (isAndroidNative || priority || !imgRef.current) {
      setIsInView(true);
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Aumentado para 100px
        threshold: 0,
      }
    );

    observerRef.current.observe(imgRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [priority, isAndroidNative]);

  // Garantir que imagens carreguem mesmo sem intersection observer
  useEffect(() => {
    if (!isInView && !priority && !isAndroidNative) {
      const t = setTimeout(() => {
        setIsInView(true);
      }, 2000); // Aumentado para 2s
      return () => clearTimeout(t);
    }
  }, [isInView, priority, isAndroidNative]);

  const normalizedSrc = (src || '').trim();
  const isPlaceholder = !normalizedSrc || hasError;
  const imageSrc = isPlaceholder ? BRAND_PLACEHOLDER : normalizedSrc;

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    setHasError(false);
    markImageLoaded(normalizedSrc);
    onLoad?.();
  }, [onLoad, normalizedSrc]);

  const handleError = useCallback(() => {
    // Retry uma vez (rápido) — depois mostra placeholder branded sem flash
    if (retryCount < 1 && normalizedSrc) {
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        if (imgElRef.current) {
          imgElRef.current.src = `${normalizedSrc}${normalizedSrc.includes('?') ? '&' : '?'}r=${Date.now()}`;
        }
      }, 600);
    } else {
      setHasError(true);
      setIsLoaded(true);
      onError?.();
    }
  }, [retryCount, onError, normalizedSrc]);

  // Limpar timeout no unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Verificar se imagem já está carregada (cache do navegador)
  useEffect(() => {
    if (!isInView || isLoaded) return;
    
    const el = imgElRef.current;
    if (!el) return;
    
    // Se já estiver completa, marca como loaded
    if (el.complete && el.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [isInView, imageSrc]);

  const aspectRatios = {
    '4/3': 'aspect-[4/3]',
    '16/9': 'aspect-[16/9]',
    '1/1': 'aspect-square',
    '3/2': 'aspect-[3/2]',
    'none': '',
  };

  const objectFits = {
    cover: 'object-cover',
    contain: 'object-contain',
    fill: 'object-fill',
  };

  return (
    <div
      ref={imgRef}
      className={cn(
        'relative overflow-hidden',
        aspectRatios[aspectRatio],
        containerClassName
      )}
      style={{ backgroundColor: placeholderColor }}
    >
      {/* Placeholder estático (sem pulse, sem flash) — só visível enquanto a imagem real carrega */}
      <div
        className={cn(
          'absolute inset-0 bg-zinc-100 dark:bg-zinc-900',
          'transition-opacity duration-150 flex items-center justify-center pointer-events-none',
          isLoaded ? 'opacity-0' : 'opacity-100'
        )}
      >
        <Home className="w-6 h-6 text-zinc-200 dark:text-zinc-800" />
      </div>


      {/* Imagem real (ou placeholder branded em caso de erro) — sempre renderizada */}
      {(isInView || priority) && (
        <img
          key={hasError ? 'placeholder' : normalizedSrc}
          ref={(node) => {
            imgElRef.current = node;
          }}
          src={imageSrc}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          // @ts-expect-error fetchpriority é atributo HTML válido
          fetchpriority={priority ? 'high' : 'auto'}
          sizes={sizes}
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            'w-full h-full transition-opacity duration-200',
            objectFits[objectFit],
            isLoaded ? 'opacity-100' : 'opacity-0',
            className
          )}
        />
      )}
    </div>
  );
});

OptimizedImage.displayName = 'OptimizedImage';

export default OptimizedImage;
